import { observer } from "mobx-react-lite";
import { FC, useCallback } from "react";

import { unique } from "@bps/common-utils";
import { DateTime, TIME_FORMATS } from "@bps/date-time";
import {
  IColumn,
  NoDataTile,
  ScrollablePane,
  Stack,
  Text
} from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import {
  OutboundCommDto,
  OutboundCommType
} from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameof } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { InfiniteScrollListProps } from "@ui-components/InfiniteScrollList/InfiniteScrollList.types.ts";

import { outboundCommReasonText } from "../components/CommunicationHistory.types.ts";
import { CommunicationStatusCell } from "../components/CommunicationStatusCell.tsx";
import {
  CommunicationHistoryFilter,
  CommunicationHistoryFilterState
} from "./CommunicationHistoryFilter.tsx";

const CommunicationHistoryListInner: FC<{
  filter: CommunicationHistoryFilterState;
  reset: () => void;
}> = observer(({ filter, reset }) => {
  const { practice, comms } = useStores();
  // Different out bound comm use differnt field for patient id, for ExportPatientRecord, sourceId is the patient id, for Claim/ClaimAdjustment, patientId is patient id, for others, contactId is patient id.
  const getPatients = (item: OutboundCommDto) => {
    if (item.outboundCommTypeCode === OutboundCommType.ExportPatientRecord) {
      return (
        <Text>{practice.contactsMap.get(item.sourceId)?.nameWithTitle}</Text>
      );
    } else if (
      item.outboundCommTypeCode === OutboundCommType.Claim ||
      item.outboundCommTypeCode === OutboundCommType.ClaimAdjustment
    ) {
      return (
        <Text>
          {item.patientId !== undefined
            ? practice.contactsMap.get(item.patientId)?.nameWithTitle
            : ""}
        </Text>
      );
    } else {
      return (
        <Text>{practice.contactsMap.get(item.contactId)?.nameWithTitle}</Text>
      );
    }
  };

  const columns: IColumn[] = [
    {
      name: "Date",
      fieldName: "Date",
      key: "Date",
      onRender: (item: OutboundCommDto) => (
        <Text>
          {DateTime.fromISO(item.changeLog?.createdDate)?.toDayDefaultFormat()}
        </Text>
      ),
      minWidth: 90,
      maxWidth: 90
    },
    {
      name: "Time",
      fieldName: "Time",
      key: "Time",
      onRender: (item: OutboundCommDto) => (
        <Text>
          {DateTime.fromISO(item.changeLog?.createdDate)?.toFormat(
            TIME_FORMATS.DEFAULT_TIME_FORMAT
          )}
        </Text>
      ),
      minWidth: 90,
      maxWidth: 90
    },
    {
      name: `${usePatientLabel(true)}`,
      fieldName: "Patient",
      key: "Patient",
      onRender: getPatients,
      minWidth: 120,
      maxWidth: 320
    },
    {
      name: "Status",
      fieldName: nameof<OutboundCommDto>("statusCode"),
      key: nameof<OutboundCommDto>("statusCode"),
      onRender: (item: OutboundCommDto) => (
        <CommunicationStatusCell item={item} />
      ),
      minWidth: 120,
      maxWidth: 180
    },
    {
      name: "Reason",
      fieldName: nameof<OutboundCommDto>("outboundCommTypeCode"),
      key: nameof<OutboundCommDto>("outboundCommTypeCode"),
      onRender: (item: OutboundCommDto) => (
        <Text>{outboundCommReasonText[item.outboundCommTypeCode]}</Text>
      ),
      minWidth: 200,
      maxWidth: 280
    },
    {
      name: "Method",
      fieldName: nameof<OutboundCommDto>("outboundCommChannelCode"),
      key: nameof<OutboundCommDto>("outboundCommChannelCode"),
      onRender: (item: OutboundCommDto) => (
        <Text>{item.outboundCommChannelCode}</Text>
      ),
      minWidth: 100,
      maxWidth: 160
    },
    {
      name: "Created by",
      fieldName: "Created by",
      key: "Created by",
      onRender: (item: OutboundCommDto) => {
        const sender = item.sender;
        return sender && <Text>{sender}</Text>;
      },
      minWidth: 200,
      maxWidth: 320
    }
  ];

  const getItems: InfiniteScrollListProps<OutboundCommDto>["getItems"] =
    useCallback(
      async (query: PagingOptions) => {
        if (!filter.search || filter.search === "") {
          const nofilterResult = await comms.getOutboundCommHistory({
            ...query,
            take: 25
          });

          const patientIds = nofilterResult.results.map(x => {
            if (
              x.outboundCommTypeCode === OutboundCommType.ExportPatientRecord
            ) {
              return x.sourceId;
            } else if (
              x.outboundCommTypeCode === OutboundCommType.Claim ||
              x.outboundCommTypeCode === OutboundCommType.ClaimAdjustment
            ) {
              return x.patientId;
            } else {
              return x.contactId;
            }
          });

          // preload contacts' names
          await practice.getContactsById(
            unique(patientIds.filter((id): id is string => id !== undefined))
          );

          const filterACCResult = nofilterResult.results.filter(
            item => item.outboundCommAddressValue !== "claims@acc.co.nz"
          );

          return {
            ...nofilterResult,
            results: filterACCResult
          };
        }

        const contactsResult = await practice.fetchContacts({
          filter: {
            search: filter.search
          }
        });

        const contactIds = contactsResult.results
          .filter(x => x.type !== ContactType.Organisation)
          .map(contact => contact.id);

        const filterResult = await comms.getOutboundCommHistory({
          ...query,
          contactIds
        });

        const filterpatientIds = filterResult.results.map(x => {
          if (x.outboundCommTypeCode === OutboundCommType.ExportPatientRecord) {
            return x.sourceId;
          } else if (
            x.outboundCommTypeCode === OutboundCommType.Claim ||
            x.outboundCommTypeCode === OutboundCommType.ClaimAdjustment
          ) {
            return x.patientId;
          } else {
            return x.contactId;
          }
        });

        // preload contacts' names
        await practice.getContactsById(
          unique(
            filterpatientIds.filter((id): id is string => id !== undefined)
          )
        );

        return filterResult;
      },
      [filter, comms, practice]
    );

  return (
    <Stack styles={{ root: { position: "relative", flex: 1 } }}>
      <ScrollablePane>
        <InfiniteScrollList
          stickyHeader
          columns={columns}
          getItems={getItems}
          onRenderNoResults={() => (
            <NoDataTile
              styles={{ root: { boxShadow: "none" } }}
              textProps={{
                text: `No record recent interactions with ${usePatientLabel(
                  false
                )} records`
              }}
              linkProps={
                filter.search
                  ? {
                      text: "Clear filter",
                      onClick: () => reset()
                    }
                  : { hidden: true }
              }
            />
          )}
        />
      </ScrollablePane>
    </Stack>
  );
});

export const CommunicationHistoryList: FC = observer(() => (
  <Stack grow tokens={{ childrenGap: 16 }}>
    <CommunicationHistoryFilter>
      {({ values: filter }, { reset }) => (
        <CommunicationHistoryListInner filter={filter} reset={reset} />
      )}
    </CommunicationHistoryFilter>
  </Stack>
));
